/// <amd-module name="ServiceHooks/Web/Admin/Components/NotificationDetailView/NotificationDetailView" />
import * as React from "react";
import * as Resources from "ServiceHooks/Web/Admin/Resources";
import { IReadonlyObservableValue, useObservable } from "VSS/Core/Observable";
import { Card } from "VSSUI/Card";
import { Header, TitleSize } from "VSSUI/Header";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Status, StatusSize } from "VSSUI/Status";
import { renderLoadingCell, TableColumnLayout } from "VSSUI/Table";
import { Tab, TabBar } from "VSSUI/Tabs";
import { TextField } from "VSSUI/TextField";
import { ServiceHooksDataContext } from "../../Context/ServiceHooksDataContext";
import { Notification } from "../../Contracts/ServiceHooks";
import { getNotificationStatusBundle, sanitizeLinks } from "../../Utilities";
import { INotificationDetailViewProps } from "./NotificationDetailView.Props";

function getTabContentFromId(selectedTabId: string, notification: Notification) {
    switch (selectedTabId) {
        case "event":
            return <NotificationMessageRenderer key="event" value={JSON.stringify(notification.details.event, null, 2)} />;
        case "request":
            return <NotificationMessageRenderer key="request" value={notification.details.request} />;
        case "response":
            return <NotificationMessageRenderer key="response" value={notification.details.response} />;
        default:
            return null;
    }
}

export const NotificationDetailView: React.FunctionComponent<INotificationDetailViewProps> = props => {
    const { onBackClick, onGetNotificationDetails, notificationId, subscription } = props;
    const dataContext = React.useContext(ServiceHooksDataContext);

    const [selectedTabId, setSelectedTabId] = useObservable("event");
    const [notification, setNotification] = useObservable<Notification | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useObservable<string>("");

    React.useEffect(() => {
        onGetNotificationDetails(subscription.id, notificationId)
            .then(result => {
                setNotification(result);
            })
            .catch(reason => {
                setErrorMessage(reason.message as string);
            });
    }, []);

    const consumer = dataContext.consumersById[subscription.consumerId];
    const actionName = dataContext.consumerActionsById[subscription.consumerId + subscription.consumerActionId]
        ? dataContext.consumerActionsById[subscription.consumerId + subscription.consumerActionId].name
        : Resources.Unknown;

    return (
        <React.Fragment>
            <Header
                backButtonProps={{ onClick: onBackClick }}
                description={`${consumer.name} (${actionName})`}
                title={`${Resources.DataNotification} ${notificationId}`}
                titleSize={TitleSize.Large}
            />

            <div className="page-content page-content-top flex-column flex-grow rhythm-vertical-8">
                <Observer errorMessage={errorMessage}>
                    {(observerProps: { errorMessage: string }) => {
                        return observerProps.errorMessage.length > 0 ? (
                            <MessageCard severity={MessageCardSeverity.Error}>{sanitizeLinks(observerProps.errorMessage)}</MessageCard>
                        ) : null;
                    }}
                </Observer>

                <NotificationDetailCard notification={notification} />

                <TabBar selectedTabId={selectedTabId} onSelectedTabChanged={setSelectedTabId}>
                    <Tab id="event" name={Resources.TestSubscriptionTabEventTitle} />
                    <Tab id="request" name={Resources.TestSubscriptionTabRequestTitle} />
                    <Tab id="response" name={Resources.TestSubscriptionTabResponseTitle} />
                </TabBar>

                <Observer notification={notification} selectedTabId={selectedTabId}>
                    {(observerProps: { notification: Notification | undefined; selectedTabId: string }) =>
                        observerProps.notification ? getTabContentFromId(observerProps.selectedTabId, observerProps.notification) : null
                    }
                </Observer>
            </div>
        </React.Fragment>
    );
};

const NotificationDetailCard: React.FunctionComponent<{ notification: IReadonlyObservableValue<Notification | undefined> }> = props => {
    const { notification } = props;

    return (
        <Card className="notification-details-card" contentProps={{ className: "flex-row" }}>
            <Observer notification={notification}>
                {(observerProps: { notification: Notification | undefined }) => {
                    return (
                        <React.Fragment>
                            <div className="hooks-notification-detail-group flex-column">
                                <div className="title-xs">{Resources.TestSubscriptionTabSummaryStatus}</div>
                                <div className="flex-row flex-center">
                                    {!observerProps.notification ? (
                                        renderLoadingCell(TableColumnLayout.singleLinePrefix)
                                    ) : (
                                        <React.Fragment>
                                            <Status
                                                {...getNotificationStatusBundle(observerProps.notification).statusProps}
                                                className="hooks-test-dialog-status flex-self-center icon-margin"
                                                size={StatusSize.m}
                                            />
                                            <div className="body-m flex-self-center">
                                                {getNotificationStatusBundle(observerProps.notification).label}
                                            </div>
                                        </React.Fragment>
                                    )}
                                </div>
                            </div>
                            <div className="hooks-notification-detail-group flex-column">
                                <div className="title-xs">{Resources.HistoryTableSentAtHeader}</div>
                                <div className="body-m">
                                    {!observerProps.notification
                                        ? renderLoadingCell(TableColumnLayout.singleLine)
                                        : observerProps.notification.createdDate.toLocaleString()}
                                </div>
                            </div>
                            <div className="hooks-notification-detail-group flex-column">
                                <div className="title-xs">{Resources.TestSubscriptionTabSummaryMessage}</div>
                                {!observerProps.notification ? (
                                    renderLoadingCell(TableColumnLayout.singleLine)
                                ) : (
                                    <div className="body-m">
                                        {sanitizeLinks(
                                            observerProps.notification.details.event.message
                                                ? observerProps.notification.details.event.message.html
                                                : ""
                                        )}
                                    </div>
                                )}
                            </div>
                        </React.Fragment>
                    );
                }}
            </Observer>
        </Card>
    );
};

const NotificationMessageRenderer: React.FunctionComponent<{ value: string }> = props => {
    return (
        <TextField
            className="hooks-notification-message-renderer flex-grow"
            containerClassName="flex-grow"
            inputClassName="flex-self-stretch"
            multiline={true}
            readOnly={true}
            resizable={false}
            value={props.value}
        />
    );
};
